import type { PrV2GroupedCodeContent } from '../../file-tree-v2/pr-v2-file-tree-utils';

import {
  getPrV2ChapterKey,
  getPrV2GroupElementId,
  PR_V2_CHAPTER_MODE_FILE_THRESHOLD,
} from '../../file-tree-v2/pr-v2-file-tree-utils';

export type PrV2ChapterEntry = {
  key: string;
  group: PrV2GroupedCodeContent;
  sectionIndex: number;
  firstFilename: string | null;
};

export type PrV2ChapterModeState = {
  activeChapterKey: string | null;
  anchorFilename: string | null;
  comparisonKey: string | null;
};

export type PrV2ChapterModeModel = {
  chapterEntries: PrV2ChapterEntry[];
  chapterEntryByKey: Map<string, PrV2ChapterEntry>;
  chapterKeyByFilename: Map<string, string>;
  chapterKeyByGroupId: Map<string, string>;
};

export const EMPTY_PR_V2_CHAPTER_MODE_STATE: PrV2ChapterModeState = {
  activeChapterKey: null,
  anchorFilename: null,
  comparisonKey: null,
};

/**
 * True when the PR itself is large enough to paginate by group. Pass the
 * unfiltered file and group counts — a visibility filter does not shrink the PR.
 */
export function isPrV2LargePrChapterMode(fileCount: number, groupCount: number): boolean {
  return fileCount > PR_V2_CHAPTER_MODE_FILE_THRESHOLD && groupCount > 1;
}

export function buildPrV2ChapterModeModel(
  groupedCodeContent: PrV2GroupedCodeContent[],
): PrV2ChapterModeModel {
  const chapterEntries: PrV2ChapterEntry[] = [];
  const chapterEntryByKey = new Map<string, PrV2ChapterEntry>();
  const chapterKeyByFilename = new Map<string, string>();
  const chapterKeyByGroupId = new Map<string, string>();

  groupedCodeContent.forEach((group, groupIndex) => {
    const key = getPrV2ChapterKey(group.groupName, group.groupOrder);
    const entry = {
      key,
      group,
      sectionIndex: groupIndex + 1,
      firstFilename: group.files[0]?.filename ?? null,
    };

    chapterEntries.push(entry);
    chapterEntryByKey.set(key, entry);
    chapterKeyByGroupId.set(getPrV2GroupElementId(group.groupName, group.groupOrder), key);
    group.files.forEach((file) => {
      chapterKeyByFilename.set(file.filename, key);
    });
  });

  return {
    chapterEntries,
    chapterEntryByKey,
    chapterKeyByFilename,
    chapterKeyByGroupId,
  };
}

export function resolvePrV2ActiveChapterEntry({
  chapterComparisonKey,
  chapterEntries,
  chapterEntryByKey,
  chapterKeyByFilename,
  chapterModeState,
  isLargePrChapterMode,
}: {
  chapterComparisonKey: string;
  chapterEntries: PrV2ChapterEntry[];
  chapterEntryByKey: Map<string, PrV2ChapterEntry>;
  chapterKeyByFilename: Map<string, string>;
  chapterModeState: PrV2ChapterModeState;
  isLargePrChapterMode: boolean;
}): PrV2ChapterEntry | null {
  if (!isLargePrChapterMode) {
    return null;
  }

  const canUseStoredChapter = chapterModeState.comparisonKey === chapterComparisonKey;
  if (canUseStoredChapter && chapterModeState.activeChapterKey) {
    const activeEntry = chapterEntryByKey.get(chapterModeState.activeChapterKey);
    if (activeEntry) {
      return activeEntry;
    }
  }

  const anchoredChapterKey =
    canUseStoredChapter && chapterModeState.anchorFilename
      ? (chapterKeyByFilename.get(chapterModeState.anchorFilename) ?? null)
      : null;
  if (anchoredChapterKey) {
    return chapterEntryByKey.get(anchoredChapterKey) ?? null;
  }

  return chapterEntries[0] ?? null;
}
